import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const SSO_ENABLED = !!process.env.REACT_APP_SF_CLIENT_ID;
const API_URL = process.env.REACT_APP_API_URL;

function destFor(role) {
  if (role === 'pending') return '/pending';
  if (role === 'records') return '/records';
  if (role === 'upload') return '/dashboard';
  if (role === 'gis') return '/gis';
  return '/dashboard';
}

export default function Login() {
  const { login, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('sso_token');
    const ssoUser = params.get('sso_user');
    const ssoError = params.get('error');

    if (ssoError) {
      setError('Site Tracker login failed. Please try again.');
      window.history.replaceState({}, '', '/login');
      return;
    }
    if (ssoToken && ssoUser) {
      try {
        const user = JSON.parse(decodeURIComponent(ssoUser));
        loginWithToken(ssoToken, user);
        navigate(destFor(user.role));
      } catch {
        setError('Login error. Please try again.');
      }
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      navigate(destFor(user.role));
    } catch {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  }

  function handleSSOLogin() {
    window.location.href = `${API_URL}/api/auth/sf-login`;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center" style={{
      backgroundColor: '#0d1b2e',
      backgroundImage: "url('/topo-bg.svg'), radial-gradient(ellipse at center, #142236 0%, #0d1b2e 70%)",
      backgroundRepeat: 'no-repeat, no-repeat',
      backgroundSize: 'cover, cover',
      backgroundAttachment: 'fixed, fixed',
    }}>
      <div className="mb-8 text-center">
        <img src="/img/blvd-logo.png" alt="BLVD" className="h-12 w-auto mx-auto" />
        <p className="text-gray-400 text-xs tracking-widest uppercase mt-1">Geospatial Portal</p>
        <div className="mt-2 inline-block border text-xs tracking-widest px-3 py-0.5 uppercase" style={{ borderColor: '#c9a84c', color: '#c9a84c' }}>
          Internal
        </div>
      </div>

      <div className="w-full max-w-sm px-6">
        {error && <p className="text-red-400 text-sm mb-4 text-center">{error}</p>}

        {SSO_ENABLED && (
          <>
            <button
              onClick={handleSSOLogin}
              className="w-full py-3 rounded text-sm font-bold tracking-widest uppercase hover:bg-gold-400 transition flex items-center justify-center gap-2"
              style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/>
              </svg>
              Sign in with Site Tracker
            </button>
            <div className="flex items-center gap-3 my-1">
              <div className="flex-1 h-px" style={{ backgroundColor: '#1e3a5f' }} />
              <span className="text-gray-600 text-xs uppercase tracking-widest">or</span>
              <div className="flex-1 h-px" style={{ backgroundColor: '#1e3a5f' }} />
            </div>
          </>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="email"
            required
            placeholder="Email"
            className="w-full border text-white placeholder-gray-500 rounded px-4 py-3 text-sm focus:outline-none transition"
            style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <input
            type="password"
            required
            placeholder="Password"
            className="w-full border text-white placeholder-gray-500 rounded px-4 py-3 text-sm focus:outline-none transition"
            style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded text-sm font-bold tracking-widest uppercase disabled:opacity-50 transition mt-2"
            style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>

      <p className="text-center text-xs text-gray-500 mt-8">
        &copy; {new Date().getFullYear()} BLVD LLC
      </p>
    </div>
  );
}
